export const GAME_WIDTH = 1_000;
export const GAME_HEIGHT = 520;
export const GROUND_Y = 438;
export const NET_X = GAME_WIDTH / 2;
export const NET_TOP = 274;
export const WIN_SCORE = 7;
export const FIXED_STEP_SECONDS = 1 / 60;
export const SERVE_TICKS = 45;
const POINT_TICKS = 69;

export type PlayerSide = 0 | 1;
export type GamePhase = 'ready' | 'playing' | 'point' | 'gameover';

export interface PlayerInput {
  left: boolean;
  right: boolean;
  jump: boolean;
  attack: boolean;
}

export interface PlayerState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  onGround: boolean;
  attack: number;
  contact: number;
  side: PlayerSide;
  facing: 1 | -1;
}

export interface BallState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  rotation: number;
}

export interface GameState {
  tick: number;
  players: [PlayerState, PlayerState];
  ball: BallState;
  scores: [number, number];
  phase: GamePhase;
  server: PlayerSide;
  serveTicks: number;
  pointTicks: number;
  winner: PlayerSide | null;
}

export type GameEvent =
  | { type: 'jump'; side: PlayerSide }
  | { type: 'hit'; side: PlayerSide; x: number; y: number; strength: number }
  | { type: 'smash'; side: PlayerSide; x: number; y: number }
  | { type: 'wall' }
  | { type: 'point'; side: PlayerSide; x: number; y: number }
  | { type: 'gameover'; side: PlayerSide }
  | { type: 'serve'; side: PlayerSide };

export const EMPTY_INPUT: Readonly<PlayerInput> = Object.freeze({
  left: false,
  right: false,
  jump: false,
  attack: false,
});

function makePlayer(side: PlayerSide): PlayerState {
  return {
    x: side === 0 ? 235 : 765,
    y: GROUND_Y - 48,
    vx: 0,
    vy: 0,
    onGround: true,
    attack: 0,
    contact: 0,
    side,
    facing: side === 0 ? 1 : -1,
  };
}

function makeBall(server: PlayerSide): BallState {
  return {
    x: server === 0 ? 255 : 745,
    y: 82,
    vx: server === 0 ? 45 : -45,
    vy: -80,
    radius: 24,
    rotation: 0,
  };
}

export function createGameState(): GameState {
  return {
    tick: 0,
    players: [makePlayer(0), makePlayer(1)],
    ball: makeBall(0),
    scores: [0, 0],
    phase: 'ready',
    server: 0,
    serveTicks: 0,
    pointTicks: 0,
    winner: null,
  };
}

export function cloneGameState(state: GameState): GameState {
  return {
    ...state,
    players: [{ ...state.players[0] }, { ...state.players[1] }],
    ball: { ...state.ball },
    scores: [...state.scores] as [number, number],
  };
}

export function resetRally(state: GameState, server: PlayerSide): void {
  state.players = [makePlayer(0), makePlayer(1)];
  state.server = server;
  state.ball = makeBall(server);
  state.serveTicks = SERVE_TICKS;
  state.pointTicks = 0;
}

export function startMatch(state: GameState, server: PlayerSide): void {
  state.tick = 0;
  state.scores = [0, 0];
  state.phase = 'playing';
  state.winner = null;
  resetRally(state, server);
}

function updatePlayer(
  player: PlayerState,
  input: Readonly<PlayerInput>,
  events: GameEvent[],
): void {
  const dt = FIXED_STEP_SECONDS;
  const targetVelocity = (Number(input.right) - Number(input.left)) * 315;
  player.vx += (targetVelocity - player.vx) * Math.min(1, dt * 15);
  if (!input.left && !input.right) player.vx *= Math.pow(0.002, dt);

  if (input.jump && player.onGround) {
    player.vy = -620;
    player.onGround = false;
    events.push({ type: 'jump', side: player.side });
  }

  if (input.attack) player.attack = Math.max(player.attack, 0.14);
  else player.attack = Math.max(0, player.attack - dt);
  player.contact = Math.max(0, player.contact - dt);
  player.vy += 1_450 * dt;
  player.x += player.vx * dt;
  player.y += player.vy * dt;

  const baseY = GROUND_Y - 48;
  if (player.y >= baseY) {
    player.y = baseY;
    player.vy = 0;
    player.onGround = true;
  }

  const minX = player.side === 0 ? 54 : NET_X + 64;
  const maxX = player.side === 0 ? NET_X - 64 : GAME_WIDTH - 54;
  player.x = Math.max(minX, Math.min(maxX, player.x));
  if ((player.x === minX && player.vx < 0) || (player.x === maxX && player.vx > 0)) {
    player.vx = 0;
  }
}

function collidePlayer(state: GameState, player: PlayerState, events: GameEvent[]): void {
  const ball = state.ball;
  const direction = player.side === 0 ? 1 : -1;
  const attackReach = player.attack > 0 ? 22 : 0;
  const hitCenterX = player.x + direction * attackReach;
  const centerY = player.y - 6;
  const dx = ball.x - hitCenterX;
  const dy = ball.y - centerY;
  const distance = Math.max(0.001, Math.hypot(dx, dy));
  const collisionRadius = (player.attack > 0 ? 76 : 61) + ball.radius;
  if (distance >= collisionRadius) return;

  const nx = dx / distance;
  const ny = dy / distance;
  const overlap = collisionRadius - distance;
  ball.x += nx * overlap;
  ball.y += ny * overlap;
  if (player.contact > 0) return;

  const relative = (ball.vx - player.vx) * nx + (ball.vy - player.vy) * ny;
  const impulse = Math.max(255, -relative * 1.72);
  ball.vx += nx * impulse + player.vx * 0.32;
  ball.vy += ny * impulse + player.vy * 0.28;

  if (ball.y < player.y - 8 && player.attack <= 0) {
    ball.vy = Math.min(ball.vy, -335);
  }

  if (player.attack > 0) {
    ball.vx = direction * Math.max(900, Math.abs(ball.vx) + 220);
    ball.vy = Math.max(155, Math.min(250, Math.abs(ball.vy) * 0.65));
    events.push({ type: 'smash', side: player.side, x: ball.x, y: ball.y });
  } else {
    events.push({
      type: 'hit',
      side: player.side,
      x: ball.x,
      y: ball.y,
      strength: Math.abs(relative),
    });
  }
  player.contact = 0.075;
}

function scorePoint(state: GameState, scorer: PlayerSide, events: GameEvent[]): void {
  state.scores[scorer] += 1;
  state.server = scorer;
  events.push({ type: 'point', side: scorer, x: state.ball.x, y: GROUND_Y - 10 });

  if (state.scores[scorer] >= WIN_SCORE) {
    state.phase = 'gameover';
    state.winner = scorer;
    events.push({ type: 'gameover', side: scorer });
  } else {
    state.phase = 'point';
    state.pointTicks = POINT_TICKS;
  }
}

export function stepGame(
  state: GameState,
  inputs: readonly [Readonly<PlayerInput>, Readonly<PlayerInput>],
): GameEvent[] {
  const events: GameEvent[] = [];
  state.tick += 1;

  if (state.phase === 'ready' || state.phase === 'gameover') return events;

  if (state.phase === 'point') {
    state.pointTicks = Math.max(0, state.pointTicks - 1);
    if (state.pointTicks === 0) {
      resetRally(state, state.server);
      state.phase = 'playing';
      events.push({ type: 'serve', side: state.server });
    }
    return events;
  }

  updatePlayer(state.players[0], inputs[0], events);
  updatePlayer(state.players[1], inputs[1], events);

  const ball = state.ball;
  if (state.serveTicks > 0) {
    state.serveTicks -= 1;
    ball.rotation += FIXED_STEP_SECONDS * 0.8;
    return events;
  }

  const substeps = 2;
  const step = FIXED_STEP_SECONDS / substeps;
  for (let index = 0; index < substeps; index += 1) {
    ball.vy += 970 * step;
    ball.x += ball.vx * step;
    ball.y += ball.vy * step;
    ball.rotation += ball.vx * step * 0.005;

    if (ball.x - ball.radius < 0) {
      ball.x = ball.radius;
      ball.vx = Math.abs(ball.vx) * 0.82;
      events.push({ type: 'wall' });
    } else if (ball.x + ball.radius > GAME_WIDTH) {
      ball.x = GAME_WIDTH - ball.radius;
      ball.vx = -Math.abs(ball.vx) * 0.82;
      events.push({ type: 'wall' });
    }

    if (ball.y - ball.radius < 0) {
      ball.y = ball.radius;
      ball.vy = Math.abs(ball.vy) * 0.78;
    }

    const touchesNet = Math.abs(ball.x - NET_X) < ball.radius + 9
      && ball.y + ball.radius > NET_TOP;
    if (touchesNet) {
      if (ball.y < NET_TOP + ball.radius && ball.vy > 0) {
        ball.y = NET_TOP - ball.radius;
        ball.vy = -Math.abs(ball.vy) * 0.78;
      } else if (ball.x < NET_X) {
        ball.x = NET_X - ball.radius - 9;
        ball.vx = -Math.abs(ball.vx) * 0.78;
      } else {
        ball.x = NET_X + ball.radius + 9;
        ball.vx = Math.abs(ball.vx) * 0.78;
      }
    }

    collidePlayer(state, state.players[0], events);
    collidePlayer(state, state.players[1], events);
  }

  const speed = Math.hypot(ball.vx, ball.vy);
  if (speed > 1_050) {
    ball.vx *= 1_050 / speed;
    ball.vy *= 1_050 / speed;
  }

  if (ball.y + ball.radius >= GROUND_Y) {
    ball.y = GROUND_Y - ball.radius;
    scorePoint(state, ball.x < NET_X ? 1 : 0, events);
  }

  return events;
}

export function cpuInput(state: GameState): PlayerInput {
  const player = state.players[1];
  const ball = state.ball;
  const forecast = ball.x + ball.vx * 0.22;
  const target = ball.x > NET_X - 35 ? Math.max(570, Math.min(930, forecast)) : 760;
  const close = Math.abs(ball.x - player.x) < 118;
  return {
    left: target < player.x - 15,
    right: target > player.x + 15,
    jump: player.onGround && close && ball.y < player.y - 45 && ball.vy > -280,
    attack: !player.onGround && close && ball.y < player.y + 15 && ball.x > NET_X,
  };
}
